import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  clearSsoClientSession,
  exchangeCodeForToken,
  getUserDetails,
  parseAccessTokenFromPayload,
  setStoredAccessToken,
  writeProfileCache,
} from "@/services/ssoApi";
import { baseUrl } from "@/config/api";
import { SSO_RETURN_PATH_KEY } from "@/config/sso";

export default function SsoCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const code = searchParams.get("code")?.trim() ?? "";

  useEffect(() => {
    if (!code) {
      setError("Missing authorization code. Please sign in again.");
      return;
    }
    if (!baseUrl) {
      setError("App is not configured with a backend URL (VITE_API_BASE_URL). Cannot complete sign-in.");
      return;
    }
    const ac = new AbortController();
    let cancelled = false;

    (async () => {
      try {
        const data = await exchangeCodeForToken(code, ac.signal);
        const token = parseAccessTokenFromPayload(data);
        if (!token) {
          const keys =
            data && typeof data === "object" && !Array.isArray(data)
              ? Object.keys(data as object).join(", ")
              : "(non-object)";
          throw new Error(
            `No bearer token in SSO response (top-level keys: ${keys}). If your API uses another field name, extend parseAccessTokenFromPayload in ssoApi.ts.`
          );
        }
        if (cancelled) return;
        setStoredAccessToken(token);
        const payload = data as Record<string, unknown>;
        const serverEmail = typeof payload.user_email === "string" ? payload.user_email.trim().toLowerCase() : null;
        const serverName =
          typeof payload.user_display_name === "string" ? payload.user_display_name.trim() : "";
        const serverProfile = payload.profile;
        if (typeof serverProfile === "object" && serverProfile !== null && !Array.isArray(serverProfile)) {
          writeProfileCache(token, serverProfile as Record<string, unknown>);
        } else if (serverEmail) {
          writeProfileCache(token, { email: serverEmail, name: serverName || serverEmail });
        } else {
          const profile = await getUserDetails(token);
          if (
            !profile ||
            (typeof profile === "object" && "error" in profile && profile.error === "invalid_token")
          ) {
            clearSsoClientSession();
            throw new Error(
              "Could not establish your session (no profile from sign-in). Check SSO profile URL and network.",
            );
          }
        }
        if (cancelled) return;
        const next =
          SSO_RETURN_PATH_KEY && sessionStorage.getItem(SSO_RETURN_PATH_KEY)
            ? sessionStorage.getItem(SSO_RETURN_PATH_KEY)!
            : "/voice";
        if (SSO_RETURN_PATH_KEY) {
          sessionStorage.removeItem(SSO_RETURN_PATH_KEY);
        }
        navigate(next, { replace: true });
      } catch (e) {
        if (cancelled || ac.signal.aborted) return;
        if (e instanceof DOMException && e.name === "AbortError") return;
        setError(e instanceof Error ? e.message : "Sign-in failed");
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [code, navigate]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 text-center">
      {error ? (
        <>
          <h1 className="mb-2 text-lg font-semibold text-slate-800">Sign-in error</h1>
          <p className="max-w-md text-sm text-slate-600">{error}</p>
        </>
      ) : (
        <>
          <p className="text-slate-700">Completing sign-in…</p>
        </>
      )}
    </div>
  );
}
